/*
  Free shipping — currency conversion for multi-currency markets.

  Only used when the visitor's currency differs from the currency the threshold
  is typed in (free_shipping.currency), e.g. a Swiss visitor (CHF) in the
  Europe market whose threshold is 410 EUR. Liquid can't convert currencies,
  so it outputs the threshold in its own currency and this script converts it
  using only Shopify's own exchange rates:

    Shopify.currency.rate on any page = store currency (AUD) -> visitor currency.
    For the threshold's currency (EUR) we read the same value from a page
    "seen from" a country that uses it: /cart?country=AT, fetched WITHOUT
    cookies so the visitor's own market/currency never changes.

    EUR -> CHF = (EUR -> AUD) x (AUD -> CHF) = cents / rateEUR x rateCHF

  Markup it looks for (rendered by snippets/free-shipping-config.liquid and
  snippets/cart-items.liquid):
    <span data-fs-amount data-fs-base-cents="41000" data-fs-currency="EUR"
          data-fs-rate-country="AT">€410</span>
    <div data-fs-bar data-fs-base-cents="41000" data-fs-currency="EUR"
         data-fs-rate-country="AT" data-fs-cart-total="12345" hidden>
      <span data-fs-remaining-msg hidden>… <strong data-fs-remaining></strong> …</span>
      <span data-fs-achieved-msg hidden>…</span>
      <div data-fs-progress></div>
    </div>
*/
(function () {
  if (window.__freeShippingInit) return;
  window.__freeShippingInit = true;

  var CURRENCY_RE = /Shopify\.currency\s*=\s*(\{[^}]*\})/;

  // Shopify's rate store currency -> X, per currency code.
  // A number once known, false if the lookup failed. Missing = not asked yet.
  var rates = {};
  var loading = {};

  // Rate store currency -> the visitor's currency (already on this page)
  function visitorRate() {
    var r = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
    return r > 0 ? r : null;
  }

  // Reads a response only until Shopify.currency appears (~6 KB in), then
  // cancels the rest of the download (the full page is ~775 KB).
  function readUntilCurrency(res) {
    if (!res.body || !window.TextDecoder) return res.text();
    var reader = res.body.getReader();
    var decoder = new TextDecoder();
    var text = '';
    function next() {
      return reader.read().then(function (chunk) {
        if (chunk.value) text += decoder.decode(chunk.value, { stream: true });
        if (chunk.done || CURRENCY_RE.test(text) || text.length > 200000) {
          reader.cancel().catch(function () {});
          return text;
        }
        return next();
      });
    }
    return next();
  }

  function loadRate(currency, country) {
    if (loading[currency]) return;

    // Already fetched earlier in this visit
    try {
      var cached = parseFloat(sessionStorage.getItem('fs-rate-' + currency));
      if (cached > 0) {
        rates[currency] = cached;
        return;
      }
    } catch (e) {}

    loading[currency] = true;
    fetch('/cart?country=' + encodeURIComponent(country), { credentials: 'omit' })
      .then(readUntilCurrency)
      .then(function (text) {
        var match = text.match(CURRENCY_RE);
        var data = match && JSON.parse(match[1]);
        // Only trust it if that page really was in the currency we asked for
        var rate = data && data.active === currency && parseFloat(data.rate);
        if (!(rate > 0)) throw new Error('No ' + currency + ' rate found');
        rates[currency] = rate;
        try {
          sessionStorage.setItem('fs-rate-' + currency, String(rate));
        } catch (e) {}
      })
      .catch(function () {
        rates[currency] = false;
      })
      .then(function () {
        delete loading[currency];
        apply(); // render whatever was waiting for this rate
      });
  }

  // Threshold cents in its own currency -> visitor-currency cents, rounded to
  // a whole unit (e.g. 390 CHF). undefined = rate still loading; null = can't convert.
  function toVisitorCents(el) {
    var currency = el.dataset.fsCurrency;
    if (!(currency in rates)) {
      loadRate(currency, el.dataset.fsRateCountry);
      if (!(currency in rates)) return undefined; // not cached: wait for the fetch
    }
    var from = rates[currency];
    var to = visitorRate();
    if (!from || !to) return null;
    return Math.round(((+el.dataset.fsBaseCents / from) * to) / 100) * 100;
  }

  // Uses the theme's own money formatter so output matches the rest of the store
  function format(cents, dropZeros) {
    var out;
    try {
      out = window.formatMoney(cents, window.theme.moneyFormat);
    } catch (e) {
      out = (cents / 100).toFixed(2);
    }
    // Same idea as Liquid's money_without_trailing_zeros: "410,00 €" -> "410 €"
    return dropZeros ? out.replace(/[.,]00(?!\d)/, '') : out;
  }

  // Width of the bar the last time we drew it. The cart drawer throws the old
  // bar away and inserts a fresh one at width 0% on every cart change; without
  // this, the CSS transition would replay from 0 each time (the "flash").
  var lastWidth = null;

  function apply() {
    // Not ready until the theme's money formatter exists
    if (typeof window.formatMoney !== 'function') return;

    document.querySelectorAll('[data-fs-amount]:not([data-fs-done])').forEach(function (el) {
      var cents = toVisitorCents(el);
      if (cents === undefined) return; // apply() runs again when the rate arrives
      // null: keep the Liquid fallback text (e.g. "€410"), which is still true
      if (cents !== null) el.textContent = format(cents, true);
      el.setAttribute('data-fs-done', '');
    });

    document.querySelectorAll('[data-fs-bar]').forEach(function (bar) {
      // Skip bars already rendered for this exact cart total
      if (bar.getAttribute('data-fs-done') === bar.dataset.fsCartTotal) return;
      var threshold = toVisitorCents(bar);
      if (threshold === undefined) return; // apply() runs again when the rate arrives
      if (threshold === null) {
        // Can't convert: leave the bar hidden rather than show a wrong amount
        bar.setAttribute('data-fs-done', bar.dataset.fsCartTotal);
        return;
      }
      var total = +bar.dataset.fsCartTotal;
      var remaining = threshold - total;
      var remainingMsg = bar.querySelector('[data-fs-remaining-msg]');
      var achievedMsg = bar.querySelector('[data-fs-achieved-msg]');

      if (remaining > 0) {
        bar.querySelector('[data-fs-remaining]').textContent = format(remaining, false);
        remainingMsg.hidden = false;
        achievedMsg.hidden = true;
      } else {
        remainingMsg.hidden = true;
        achievedMsg.hidden = false;
      }
      var progress = bar.querySelector('[data-fs-progress]');
      var newWidth = Math.min(100, (total * 100) / threshold) + '%';

      // Jump (no animation) to where the previous bar was, then animate from
      // there to the new width. On the first draw there is no previous bar, so
      // it jumps straight to the final width, like the server-rendered bar does.
      var transition = progress.style.transition;
      progress.style.transition = 'none';
      progress.style.width = lastWidth || newWidth;
      void progress.offsetWidth; // force layout so the browser registers the start point
      progress.style.transition = transition;
      progress.style.width = newWidth;
      lastWidth = newWidth;

      bar.hidden = false;
      bar.setAttribute('data-fs-done', bar.dataset.fsCartTotal);
    });
  }

  // The cart drawer re-renders its HTML after every cart change, so watch for
  // new (unprocessed) elements instead of running once. Attribute changes are
  // watched too, in case the re-render patches the existing bar in place
  // (new cart total, data-fs-done stripped).
  // apply() runs directly in the observer callback: those callbacks run before
  // the browser paints, so the fresh (hidden, 0%) bar is never shown on screen.
  // Our own writes trigger the observer again, but apply() then finds nothing
  // left to do, so it settles after one extra pass.

  // Wait for DOMContentLoaded: window.formatMoney comes from a module script
  // that runs after this deferred one.
  if (document.readyState === 'complete') {
    apply();
  } else {
    window.addEventListener('DOMContentLoaded', apply);
  }
  new MutationObserver(apply).observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-fs-done', 'data-fs-cart-total']
  });
})();
